import React from 'react';
import { X } from 'lucide-react';
import ActionButton from '../../Buttons/ActionButton';
import InputField from '../../Forms/Input/InputField';
import { SelectedStockRow } from '../../../types/inventory/initialStockAdmin/InitialStockAdminTypes';

interface Props {
  selected: SelectedStockRow[];
  isSaving: boolean;
  onChangeValue: (
    row: Pick<SelectedStockRow, 'productId' | 'branchId'>,
    field: 'newQuantity' | 'newUnitCost',
    value: number
  ) => void;
  onRemove: (row: Pick<SelectedStockRow, 'productId' | 'branchId'>) => void;
  onSave: () => void;
  onClear: () => void;
}

const InitialStockAdminEditPanel: React.FC<Props> = ({
  selected,
  isSaving,
  onChangeValue,
  onRemove,
  onSave,
  onClear,
}) => {
  if (selected.length === 0) return null;

  return (
    <div className="mt-6 rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold">
          Corregir inventario inicial ({selected.length})
        </h3>
        <ActionButton
          label="Limpiar selección"
          variant="secondary"
          size="sm"
          fullWidth={false}
          onClick={onClear}
          disabled={isSaving}
        />
      </div>

      <div className="space-y-3">
        {selected.map((row) => (
          <div
            key={`${row.productId}-${row.branchId}`}
            className="grid grid-cols-1 items-end gap-3 rounded border border-stroke p-3 dark:border-strokedark md:grid-cols-12"
          >
            <div className="md:col-span-5">
              <div className="font-mono text-xs text-gray-500 dark:text-gray-400">
                {row.productCode}
              </div>
              <div className="font-medium text-black dark:text-white">{row.productName}</div>
              <div className="text-xs text-body dark:text-bodydark2">
                {row.branchName} — Actual: {row.stockQuantity} {row.unitCode ?? ''} @ $
                {row.averageCost.toFixed(4)}
              </div>
            </div>

            <div className="md:col-span-3">
              <InputField
                label="Nueva Cantidad"
                name={`qty-${row.productId}-${row.branchId}`}
                type="number"
                step="0.000001"
                value={row.newQuantity}
                onChange={(e) =>
                  onChangeValue(row, 'newQuantity', parseFloat(e.target.value) || 0)
                }
              />
            </div>

            <div className="md:col-span-3">
              <InputField
                label="Nuevo Costo"
                name={`cost-${row.productId}-${row.branchId}`}
                type="number"
                step="0.0001"
                min={0}
                value={row.newUnitCost}
                onChange={(e) =>
                  onChangeValue(row, 'newUnitCost', parseFloat(e.target.value) || 0)
                }
              />
            </div>

            <div className="flex justify-end md:col-span-1">
              <button
                type="button"
                className="text-danger hover:opacity-70"
                onClick={() => onRemove(row)}
                title="Quitar de la selección"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex justify-end">
        <ActionButton
          label="Guardar Cambios"
          variant="success"
          fullWidth={false}
          loading={isSaving}
          loadingLabel="Guardando..."
          onClick={onSave}
        />
      </div>
    </div>
  );
};

export default InitialStockAdminEditPanel;
